import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from 'components/AppIcon';
// Safe regex helpers — never use ?.test() on a regex literal
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const handleSubmit = async (e) => {
  e?.preventDefault();
  const errs = validate();
  if (Object.keys(errs)?.length > 0) {
    setErrors(errs);
    return;
  }

  setLoading(true);
  setAuthError('');

  try {
    const { data, error } = await supabase?.auth?.signInWithPassword({
      email: form?.email,
      password: form?.password,
    });

    if (error) {
      const msg = (error?.message || '')?.toLowerCase();
      if (msg?.includes('invalid login credentials') || msg?.includes('invalid credentials') || msg?.includes('invalid email or password')) {
        setAuthError('Invalid email or password. Please check your credentials and try again.');
      } else if (msg?.includes('email not confirmed')) {
        setAuthError('Your email is not confirmed. Please contact support or use the demo account.');
      } else if (msg?.includes('too many requests') || msg?.includes('rate limit')) {
        setAuthError('Too many sign-in attempts. Please wait a few minutes and try again.');
      } else if (msg?.includes('network') || msg?.includes('fetch')) {
        setAuthError('Network error. Please check your internet connection.');
      } else {
        setAuthError(error?.message || 'Sign in failed. Please try again.');
      }
      setLoading(false);
      return;
    }

    if (data && (data?.session || data?.user)) {
      const from = (location?.state && location?.state?.from && location?.state?.from?.pathname) || '/dashboard';
      navigate(from, { replace: true });
    } else {
      setAuthError('Sign in failed. No session returned. Please try again.');
      setLoading(false);
    }
  } catch (err) {
    console.error('[SignIn] Unexpected error:', err);
    const msg = (err?.message || '')?.toLowerCase();
    if (msg?.includes('network') || msg?.includes('fetch') || msg?.includes('failed to fetch')) {
      setAuthError('Network error. Please check your internet connection and try again.');
    } else if (msg?.includes('invalid') || msg?.includes('credentials')) {
      setAuthError('Invalid email or password. Please try again.');
    } else {
      setAuthError(err?.message || 'Sign in failed. Please try again.');
    }
    setLoading(false);
  }
};


const SignInForm = () => {
  console.log('[SignInForm] SignInForm rendered');
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const locationMessage = location?.state?.message || '';

  const [form, setForm] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState('');
  const [resetError, setResetError] = useState('');

  const validate = () => {
    console.log('[SignInForm] validate() called');
    const errs = {};
    const emailVal = (form?.email || '')?.trim();
    const passVal = form?.password || '';
    if (!emailVal) {
      errs.email = 'Email is required';
    } else {
      console.log('[SignInForm] Validating email with value:', emailVal);
      console.log('[SignInForm] About to call EMAIL_RE.test() — typeof EMAIL_RE:', typeof EMAIL_RE);
      try {
        const emailValid = EMAIL_RE?.test(emailVal);
        console.log('[SignInForm] EMAIL_RE.test() result:', emailValid);
        if (!emailValid) {
          errs.email = 'Invalid email format';
        }
      } catch (err) {
        console.error('[SignInForm] EMAIL_RE.test() THREW AN ERROR:', err?.name, err?.message, err?.stack);
        errs.email = 'Email validation error';
      }
    }
    console.log('[SignInForm] About to validate password — passVal length:', passVal?.length);
    if (!passVal) {
      errs.password = 'Password is required';
    }
    console.log('[SignInForm] validate() returning errors:', errs);
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors?.[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (authError) setAuthError('');
  };

  const handleBlur = (e) => {
    const { name, value } = e?.target;
    if (name === 'email' && value) {
      console.log('[SignInForm] handleBlur email — About to call EMAIL_RE.test() on:', value?.trim());
      try {
        const valid = EMAIL_RE?.test(value?.trim());
        console.log('[SignInForm] handleBlur EMAIL_RE.test() result:', valid);
        if (!valid) {
          setErrors((prev) => ({ ...prev, email: 'Invalid email format' }));
        }
      } catch (err) {
        console.error('[SignInForm] handleBlur EMAIL_RE.test() THREW:', err?.name, err?.message, err?.stack);
      }
    }
    if (name === 'password' && !value) {
      setErrors((prev) => ({ ...prev, password: 'Password is required' }));
    }
  };

  const fillDemoCredentials = () => {
    setForm({ email: 'player@empirex.com', password: 'EmpireX2026!' });
    setErrors({});
    setAuthError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    console.log('[SignInForm] handleSubmit called — email:', form?.email);
    try {
      const errs = validate();
      if (Object.keys(errs)?.length > 0) {
        setErrors(errs);
        return;
      }

      setLoading(true);
      setAuthError('');

      if (typeof signIn !== 'function') {
        console.error('[SignInForm] signIn is not a function — typeof:', typeof signIn);
        setAuthError('Authentication service unavailable. Please refresh the page.');
        setLoading(false);
        return;
      }

      console.log('[SignInForm] About to call supabase.auth.signInWithPassword for email:', form?.email);
      const result = await signIn(form?.email, form?.password);
      const data = result ? result?.data : null;
      const error = result ? result?.error : null;

      if (error) {
        const msg = (error?.message || '')?.toLowerCase();
        if (msg?.includes('invalid login credentials') || msg?.includes('invalid credentials') || msg?.includes('invalid email or password')) {
          setAuthError('Invalid email or password. Please check your credentials and try again.');
        } else if (msg?.includes('email not confirmed')) {
          setAuthError('Your email is not confirmed. Please contact support or use the demo account.');
        } else if (msg?.includes('too many requests') || msg?.includes('rate limit')) {
          setAuthError('Too many sign-in attempts. Please wait a few minutes and try again.');
        } else if (msg?.includes('network') || msg?.includes('fetch')) {
          setAuthError('Network error. Please check your internet connection.');
        } else {
          setAuthError(error?.message || 'Sign in failed. Please try again.');
        }
        setLoading(false);
        return;
      }

      if (data && (data?.session || data?.user)) {
        const from = (location?.state && location?.state?.from && location?.state?.from?.pathname) || '/dashboard';
        navigate(from, { replace: true });
      } else {
        setAuthError('Sign in failed. No session returned. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      console.error('[SignInForm] handleSubmit CAUGHT ERROR — name:', err?.name, '— message:', err?.message, '— stack:', err?.stack);
      const msg = (err?.message || '')?.toLowerCase();
      if (msg?.includes('network') || msg?.includes('fetch') || msg?.includes('failed to fetch')) {
        setAuthError('Network error. Please check your internet connection and try again.');
      } else if (msg?.includes('invalid') || msg?.includes('credentials')) {
        setAuthError('Invalid email or password. Please try again.');
      } else {
        setAuthError(err?.message || 'Sign in failed. Please try again.');
      }
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e?.preventDefault();
    const emailVal = (resetEmail || '')?.trim();
    console.log('[SignInForm] handlePasswordReset called — emailVal:', emailVal);
    console.log('[SignInForm] handlePasswordReset: About to call EMAIL_RE.test() — typeof EMAIL_RE:', typeof EMAIL_RE);
    try {
      const emailValid = EMAIL_RE?.test(emailVal);
      console.log('[SignInForm] handlePasswordReset EMAIL_RE.test() result:', emailValid);
      if (!emailVal || !emailValid) {
        setResetError('Please enter a valid email address.');
        return;
      }
    } catch (err) {
      console.error('[SignInForm] handlePasswordReset EMAIL_RE.test() THREW:', err?.name, err?.message, err?.stack);
      setResetError('Email validation error. Please try again.');
      return;
    }
    setResetLoading(true);
    setResetError('');
    setResetMsg('');
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(
        emailVal?.toLowerCase(),
        { redirectTo: `${window.location?.origin}/reset-password` }
      );
      if (error) throw error;
      setResetMsg('Password reset link sent! Check your email inbox.');
    } catch (err) {
      const msg = (err?.message || '')?.toLowerCase();
      if (msg?.includes('rate limit') || msg?.includes('too many')) {
        setResetError('Too many requests. Please wait a few minutes and try again.');
      } else {
        setResetError(err?.message || 'Failed to send reset email. Please try again.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  // ── Forgot Password View ──────────────────────────────────────────────────
  if (showForgot) {
    return (
      <div className="space-y-5">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 border border-primary/30 mb-3">
            <Icon name="KeyRound" size={24} color="var(--color-primary)" />
          </div>
          <h3 className="text-base font-bold text-foreground mb-1">Reset Password</h3>
          <p className="text-sm text-muted-foreground">Enter your email and we'll send you a reset link.</p>
        </div>
        {resetMsg ? (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-success/10 border border-success/30">
            <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 shrink-0" />
            <p className="text-sm text-success">{resetMsg}</p>
          </div>
        ) : (
          <form onSubmit={handlePasswordReset} className="space-y-4" noValidate>
            {resetError && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-error/10 border border-error/30">
                <Icon name="AlertCircle" size={16} color="var(--color-error)" className="mt-0.5 shrink-0" />
                <p className="text-sm text-error">{resetError}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Email Address</label>
              <div className="relative">
                <Icon name="Mail" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e?.target?.value)}
                  placeholder="player@empirex.com"
                  autoComplete="email"
                  className="w-full bg-surface border border-border rounded-lg pl-10 pr-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={resetLoading}
              className="w-full py-3 rounded-lg bg-primary text-zinc-950 font-semibold text-sm hover:bg-primary/90 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {resetLoading ? (
                <><Icon name="Loader2" size={16} className="animate-spin" /> Sending...</>
              ) : (
                <><Icon name="Send" size={16} /> Send Reset Link</>
              )}
            </button>
          </form>
        )}
        <button
          onClick={() => { setShowForgot(false); setResetMsg(''); setResetError(''); setResetEmail(''); }}
          className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1"
        >
          <Icon name="ArrowLeft" size={14} /> Back to Sign In
        </button>
      </div>
    );
  }

  // ── Sign In View ──────────────────────────────────────────────────────────
  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate aria-label="Sign in form">
      {/* Inactivity / session message */}
      {locationMessage && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-warning/10 border border-warning/30" role="alert">
          <Icon name="Clock" size={16} color="#F59E0B" className="mt-0.5 shrink-0" />
          <p className="text-sm" style={{ color: '#F59E0B' }}>{locationMessage}</p>
        </div>
      )}
      {/* Auth Error */}
      {authError && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-error/10 border border-error/30" role="alert" aria-live="polite">
          <Icon name="AlertCircle" size={16} color="var(--color-error)" className="mt-0.5 shrink-0" />
          <p className="text-sm text-error">{authError}</p>
        </div>
      )}
      {/* Demo Credentials */}
      <div
        className="p-3 rounded-lg cursor-pointer hover:bg-primary/10 transition-all"
        style={{ background: 'rgba(0,217,255,0.05)', border: '1px solid rgba(0,217,255,0.2)' }}
        onClick={fillDemoCredentials}
        title="Click to fill demo credentials"
      >
        <p className="text-xs font-semibold text-primary mb-1">Demo Credentials <span className="font-normal text-muted-foreground">(click to fill)</span></p>
        <p className="text-xs text-muted-foreground">Email: <span className="text-foreground font-mono">player@empirex.com</span></p>
        <p className="text-xs text-muted-foreground">Password: <span className="text-foreground font-mono">EmpireX2026!</span></p>
      </div>
      {/* Email */}
      <div>
        <label htmlFor="signin-email" className="block text-sm font-medium text-foreground mb-1.5">
          Email Address
        </label>
        <div className="relative">
          <Icon name="Mail" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            id="signin-email"
            type="email"
            name="email"
            value={form?.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="player@empirex.com"
            disabled={loading}
            autoComplete="email"
            aria-label="Email address"
            aria-invalid={!!errors?.email}
            aria-describedby={errors?.email ? 'signin-email-error' : undefined}
            className={`w-full bg-surface border rounded-lg pl-10 pr-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors ${
              errors?.email ? 'border-error focus:border-error' : 'border-border focus:border-primary/50'
            } disabled:opacity-60`}
          />
        </div>
        {errors?.email && (
          <p id="signin-email-error" className="mt-1 text-xs text-error flex items-center gap-1" role="alert">
            <Icon name="AlertCircle" size={12} />
            {errors?.email}
          </p>
        )}
      </div>
      {/* Password */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label htmlFor="signin-password" className="text-sm font-medium text-foreground">Password</label>
          <button
            type="button"
            onClick={() => setShowForgot(true)}
            className="text-xs text-primary hover:text-primary/80 transition-colors"
            aria-label="Forgot password"
          >
            Forgot password?
          </button>
        </div>
        <div className="relative">
          <Icon name="Lock" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            id="signin-password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={form?.password}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="••••••••"
            disabled={loading}
            autoComplete="current-password"
            aria-label="Password"
            aria-invalid={!!errors?.password}
            aria-describedby={errors?.password ? 'signin-password-error' : undefined}
            className={`w-full bg-surface border rounded-lg pl-10 pr-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors ${
              errors?.password ? 'border-error focus:border-error' : 'border-border focus:border-primary/50'
            } disabled:opacity-60`}
          />
          <button
            type="button"
            onClick={() => setShowPassword(p => !p)}
            className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100 transition-opacity"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>
        {errors?.password && (
          <p id="signin-password-error" className="mt-1 text-xs text-error flex items-center gap-1" role="alert">
            <Icon name="AlertCircle" size={12} />
            {errors?.password}
          </p>
        )}
      </div>
      {/* Submit */}
      <button
        type="submit"
        disabled={loading || Object.values(errors)?.some(Boolean)}
        className="w-full py-3 rounded-lg font-semibold text-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        style={{ background: 'var(--color-primary)', color: 'var(--color-primary-foreground, #0a0a0a)' }}
      >
        {loading ? (
          <><Icon name="Loader2" size={16} className="animate-spin" /> Signing in...</>
        ) : (
          <><Icon name="LogIn" size={16} /> Sign In</>
        )}
      </button>
      {/* Register link */}
      <p className="text-center text-sm text-muted-foreground">
        Don't have an account?{' '}
        <Link to="/register" className="text-primary hover:text-primary/80 font-medium transition-colors">
          Create account
        </Link>
      </p>
    </form>
  );
};

export default SignInForm;
